<template>
  <div :class="{ 'input-select-addon': showCurrencySelect }">
    <span v-if="showCurrencySelect" class="input-select" :class="{ 'read-only': readOnly }">
      <FormMultiSelect
        v-if="computedCurrency"
        v-model="computedCurrency"
        :show-float-label="false"
        :config="currencySelectConfig"
      />
    </span>
    <span class="input-field">
      <FormInput
        :class="inputClasses"
        v-model="formattedValue"
        ref="inputRef"
        step="0.01"
        :rules="rules"
        :read-only="readOnly"
        :placeholder="placeholder"
        :sub-label="subLabel"
        :type="type"
        :icon="icon"
        :icon-right="iconRight"
        :show-float-label="showFloatLabel"
        :has-fixed-label="hasFixedLabel"
        :data-e2e="FormCypressAttribute.inputs.AMOUNT"
      >
        <template v-if="$slots.subLabel" #subLabel>
          <slot name="subLabel" />
        </template>
      </FormInput>
    </span>
  </div>
</template>

<script>
import { watch, ref, onMounted, nextTick } from 'vue';
import { useCurrencyInput } from 'vue-currency-input';
import FormInput from '/@/modules/core/form/components/FormInput.vue';
import FormMultiSelect from '/@/modules/core/form/components/FormMultiSelect.vue';
import { CurrencyService } from '/@/modules/predefined/services/CurrencyService';
import { CurrencySelectConfig } from '/@/modules/predefined/models/CurrencySelectConfig';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import { FormCypressAttribute } from '/@/modules/core/form/constants/FormCypressAttribute';
import Lang from '/@/modules/core/app/services/Lang';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormCurrency',
  emits: ['update:currency', 'input'],
  slotsDefinition: {
    subLabel: {}
  },
  mixins: [FormValidationMixin],
  components: {
    FormMultiSelect,
    FormInput
  },
  props: {
    currency: {
      type: Number,
      default() {
        return CurrencyService.getDefaultCurrency();
      }
    },
    value: {
      type: [Number]
    },
    showCurrencySelect: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String
    },
    subLabel: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    rules: {
      type: [Object, String]
    },
    currencyOptions: {
      type: Object,
      default: () => {}
    },
    icon: { type: String },
    iconRight: { type: String },
    hasTextRight: { type: Boolean, default: false },
    showFloatLabel: { type: Boolean, default: true },
    type: { type: String, default: 'text' },
    hasFixedLabel: { type: Boolean, default: false },
    showColours: { type: Boolean, default: false },
    invertColours: { type: Boolean, default: false }
  },
  static: {
    FormCypressAttribute
  },
  data() {
    return {
      currencySelectConfig: new CurrencySelectConfig({
        selectLabel: ''
      })
    };
  },
  computed: {
    computedCurrency: {
      get() {
        return this.currency;
      },
      set(value) {
        this.$emit('update:currency', value);
      }
    },
    inputClasses() {
      const inputClasses = { 'text-right': this.hasTextRight };

      if (!this.showColours) {
        return inputClasses;
      }

      if (this.value > 0) {
        inputClasses[this.invertColours ? 'is-negative' : 'is-positive'] = true;
      }

      if (this.value < 0) {
        inputClasses[this.invertColours ? 'is-positive' : 'is-negative'] = true;
      }

      return inputClasses;
    }
  },
  setup(props) {
    const {
      inputRef: currencyInputRef,
      formattedValue,
      numberValue,
      setOptions,
      setValue
    } = useCurrencyInput({
      currency: 'USD',
      locale: Lang.getLocaleCurrency(),
      currencyDisplay: 'hidden',
      hideCurrencySymbolOnFocus: false,
      hideGroupingSeparatorOnFocus: false,
      hideNegligibleDecimalDigitsOnFocus: false,
      autoDecimalDigits: true,
      exportValueAsInteger: false,
      autoSign: true,
      useGrouping: true,
      precision: 2,
      valueRange: { min: -999999999999, max: 999999999999 },
      ...props.currencyOptions
    });

    const inputRef = ref(null);

    // vue-currency-input requires the native input element to function correctly.
    // Since FormInput is a custom component, we need to manually find the input element
    // within it and assign it to currencyInputRef.
    const assignInputRef = async (component) => {
      if (component?.$el) {
        const inputElement = component.$el.querySelector('input');
        if (inputElement) {
          currencyInputRef.value = inputElement;
          // Wait for next tick to ensure vue-currency-input is fully initialized
          await nextTick();
          // Use setValue to properly initialize and let vue-currency-input manage the value
          if (props.value !== null && props.value !== undefined) {
            setValue(props.value);
          }
        }
      }
    };

    onMounted(() => {
      assignInputRef(inputRef.value);
    });

    watch(inputRef, (component) => {
      assignInputRef(component);
    });

    watch(
      () => props.value,
      (value) => {
        if (currencyInputRef.value && value !== null && value !== undefined) {
          setValue(value);
        }
      },
      { immediate: true }
    );

    return { inputRef, formattedValue, numberValue };
  },
  watch: {
    numberValue(newValue, oldValue) {
      // Avoid emitting on initial mount or when value hasn't actually changed
      if (newValue !== oldValue && newValue !== null && newValue !== undefined) {
        const numValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue;
        const roundedValue = !isNaN(numValue) ? Math.round(numValue * 100) / 100 : numValue;
        this.$emit('input', roundedValue);
      }
    }
  },
  mounted() {
    if (!this.currency) {
      this.computedCurrency = CurrencyService.getDefaultCurrency();
    }
  }
};
</script>

<style scoped>
span.read-only {
  pointer-events: none;
}

span.read-only :deep(.multiselect__tags) {
  background: whitesmoke !important;
}

span.read-only :deep(.multiselect__tags .multiselect__single) {
  background-color: whitesmoke !important;
}

span.read-only :deep(.multiselect__single) {
  background: whitesmoke !important;
}

:deep(.text-right input) {
  text-align: right;
}

:deep(.is-positive input) {
  color: var(--ce-green);
}

:deep(.is-negative input) {
  color: var(--ce-red);
}
</style>
